import { baseApi } from "./baseApi";
import type {
  Testimonial,
  CreateTestimonialData,
  UpdateTestimonialData,
  TestimonialFilters,
  PaginatedTestimonialsResponse,
} from "@/types/testimonial";

/**
 * Testimonial API slice
 */
export const testimonialApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create testimonial (public)
    createTestimonial: builder.mutation<
      { success: boolean; data: Testimonial; message?: string },
      CreateTestimonialData
    >({
      query: (data) => ({
        url: "/testimonials",
        method: "POST",
        body: data,
        credentials: "omit", // Public endpoint
      }),
      invalidatesTags: ["Testimonial"],
    }),

    // Get testimonials (public - approved only)
    getTestimonials: builder.query<PaginatedTestimonialsResponse, TestimonialFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.productId) searchParams.append("productId", params.productId);
        if (params?.rating) searchParams.append("rating", String(params.rating));
        if (params?.isGeneral !== undefined)
          searchParams.append("isGeneral", String(params.isGeneral));
        if (params?.search) searchParams.append("search", params.search);
        const queryString = searchParams.toString();
        return {
          url: `/testimonials${queryString ? `?${queryString}` : ""}`,
          credentials: "omit", // Public endpoint
        };
      },
      providesTags: ["Testimonial"],
    }),

    // Get testimonial by ID (public)
    getTestimonialById: builder.query<
      { success: boolean; data: Testimonial },
      string
    >({
      query: (id) => ({
        url: `/testimonials/${id}`,
        credentials: "omit", // Public endpoint
      }),
      providesTags: (_result, _error, id) => [{ type: "Testimonial", id }],
    }),

    // Create testimonial as admin
    createTestimonialAdmin: builder.mutation<
      { success: boolean; data: Testimonial; message?: string },
      CreateTestimonialData & { status?: "PENDING" | "APPROVED" | "REJECTED" }
    >({
      query: (data) => ({
        url: "/testimonials/admin",
        method: "POST",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: ["Testimonial"],
    }),

    // Update testimonial (admin)
    updateTestimonial: builder.mutation<
      { success: boolean; data: Testimonial; message?: string },
      { id: string; data: UpdateTestimonialData }
    >({
      query: ({ id, data }) => ({
        url: `/testimonials/${id}`,
        method: "PUT",
        body: data,
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Testimonial", id }, "Testimonial"],
    }),

    // Approve testimonial (admin)
    approveTestimonial: builder.mutation<
      { success: boolean; data: Testimonial; message?: string },
      string
    >({
      query: (id) => ({
        url: `/testimonials/${id}/approve`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "Testimonial", id }, "Testimonial"],
    }),

    // Reject testimonial (admin)
    rejectTestimonial: builder.mutation<
      { success: boolean; data: Testimonial; message?: string },
      string
    >({
      query: (id) => ({
        url: `/testimonials/${id}/reject`,
        method: "POST",
        credentials: "include",
      }),
      invalidatesTags: (_result, _error, id) => [{ type: "Testimonial", id }, "Testimonial"],
    }),

    // Delete testimonial (admin)
    deleteTestimonial: builder.mutation<
      { success: boolean; message?: string },
      string
    >({
      query: (id) => ({
        url: `/testimonials/${id}`,
        method: "DELETE",
        credentials: "include",
      }),
      invalidatesTags: ["Testimonial"],
    }),

    // Get pending testimonials (admin)
    getPendingTestimonials: builder.query<PaginatedTestimonialsResponse, TestimonialFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.productId) searchParams.append("productId", params.productId);
        if (params?.rating) searchParams.append("rating", String(params.rating));
        if (params?.isGeneral !== undefined)
          searchParams.append("isGeneral", String(params.isGeneral));
        if (params?.search) searchParams.append("search", params.search);
        const queryString = searchParams.toString();
        return {
          url: `/testimonials/admin/pending${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Testimonial"],
    }),

    // Get all testimonials with filters (admin)
    getAllTestimonials: builder.query<PaginatedTestimonialsResponse, TestimonialFilters | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.append("status", params.status);
        if (params?.page) searchParams.append("page", String(params.page));
        if (params?.limit) searchParams.append("limit", String(params.limit));
        if (params?.productId) searchParams.append("productId", params.productId);
        if (params?.rating) searchParams.append("rating", String(params.rating));
        if (params?.isGeneral !== undefined)
          searchParams.append("isGeneral", String(params.isGeneral));
        if (params?.search) searchParams.append("search", params.search);
        const queryString = searchParams.toString();
        return {
          url: `/testimonials/admin/all${queryString ? `?${queryString}` : ""}`,
          credentials: "include",
        };
      },
      providesTags: ["Testimonial"],
    }),

    // Get testimonial by ID (admin - can see all statuses)
    getTestimonialByIdAdmin: builder.query<
      { success: boolean; data: Testimonial },
      string
    >({
      query: (id) => ({
        url: `/testimonials/admin/${id}`,
        credentials: "include",
      }),
      providesTags: (_result, _error, id) => [{ type: "Testimonial", id }],
    }),
  }),
});

export const {
  useCreateTestimonialMutation,
  useGetTestimonialsQuery,
  useGetTestimonialByIdQuery,
  useCreateTestimonialAdminMutation,
  useUpdateTestimonialMutation,
  useApproveTestimonialMutation,
  useRejectTestimonialMutation,
  useDeleteTestimonialMutation,
  useGetPendingTestimonialsQuery,
  useGetAllTestimonialsQuery,
  useGetTestimonialByIdAdminQuery,
} = testimonialApi;
